/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    AfterViewInit,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    Injector,
    OnDestroy,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';

import {
    BehaviorSubject,
    Subscription,
} from 'rxjs';

import { attachComponentBindings } from 'nsx-alb-ui-components';
import { FullModalService, IFullModalLayout } from 'ng/modules/core/services/full-modal/full-modal.service';
import { ITEM_ID_TOKEN } from '../../shared.constants';
import { BREADCRUMB_DESCRIPTION$, BREADCRUMB_NAME$ } from './full-modal.tokens';
import './full-modal.component.less';
import { FullModalComponentService } from './full-modal-component.service';

/**
 * Breadcrumb interface. Contains properties needed to render a breadcrumb name and description.
 */
export interface IBreadcrumb {
    breadcrumbName$?: BehaviorSubject<string>,
    breadcrumbDescription$?: BehaviorSubject<string>,
    getDescription?(): string;
    getName?(): string;
}

interface IFullModalLayoutComponentRefs {
    configComponentRef: ComponentRef<Component>;
    previewComponentRef: ComponentRef<Component> | null;
}

/**
 * @description Displays a full modal that consists of a config component, breadcrumbs, and a
 *     preview component. This component subscribes to the FullModalService, which outputs a modal
 *     layout stack of modals to render.
 * @author alextsg
 */
@Component({
    selector: 'full-modal',
    templateUrl: './full-modal.component.html',
    providers: [FullModalComponentService],
})
export class FullModalComponent implements AfterViewInit, OnDestroy {
    /**
     * ViewContainerRef for fullModalConfig
     */
    @ViewChild('fullModalConfig', {
        read: ViewContainerRef,
        static: true,
    })
    private fullModalConfigViewContainerRef: ViewContainerRef;

    /**
     * ViewContainerRef for fullModalConfig preview.
     */
    @ViewChild('fullModalPreviewConfig', {
        read: ViewContainerRef,
        static: true,
    })
    private fullModalPreviewContainerRef: ViewContainerRef;

    public modalLayoutStack: IFullModalLayout[] = [];

    /**
     * List of breadcrumbs to render.
     */
    public breadcrumbStack: IBreadcrumb[] = [];

    /**
     * Map of FullModalLayout objects to Breadcrumbs. Used to share the same breadcrumb$ and breadcrumbDescription$
     * providers between the full modal and breadcrumb instances.
     */
    private breadcrumbMap: Map<IFullModalLayout, IBreadcrumb>;

    /**
     * Subscription to the FullModalService's modalLayoutStack.
     */
    private subscription: Subscription;

    /**
     * A map using the FullModalLayout as the key and its configComponentRef as the value.
     * When the user is configuring an object in a modal, and opens up another modal of another
     * object then closes that, we don't want to recreate the original modal. We do a lookup of the
     * FullModalLayout reference to see if componentRefs already exist in order to re-use
     * already-created config components.
     */
    private componentRefMap = new Map<IFullModalLayout, IFullModalLayoutComponentRefs>();

    /**
     * A map to cache scroll positions for each modal layout. Used to restore scroll position
     * when returning to a parent modal after closing a child modal.
     */
    private scrollPositionMap = new Map<IFullModalLayout, number>();

    /**
     * Tracks the previously rendered modal layout to save scroll position when switching modals.
     */
    private previousModalLayout: IFullModalLayout | null = null;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private fullModalService: FullModalService,
        private elementRef: ElementRef,
    ) {}

    /**
     * Renders a componentRef within a viewContainerRef.
     */
    private static renderComponentRef = (
        componentRef: ComponentRef<Component>,
        viewContainerRef: ViewContainerRef,
    ): void => {
        viewContainerRef.insert(componentRef.hostView);
    };

    /**
     * @override
     * Subscibes to the FullModal service, which returns a stack of FullModalLayouts to be rendered.
     */
    public ngAfterViewInit(): void {
        this.subscription = this.fullModalService.modalLayoutStack$.subscribe(this.updateModals);

        this.updateModals(this.fullModalService.modalLayoutStack);
    }

    /**
     * @override
     * Unsubscribes from the FullModal service.
     */
    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Returns true if no modals should be rendered.
     */
    public get isHidden(): boolean {
        return !this.currentModalLayout;
    }

    /**
     * Returns the current modal to be rendered.
     */
    public get currentModalLayout(): IFullModalLayout {
        return this.modalLayoutStack[this.modalLayoutStack.length - 1];
    }

    /**
     * Called when the FullModal service returns a new modalLayoutStack. Destroys componentRefs of
     * modalLayouts that no longer exist in the new modalLayoutStack, and adds new ones to the
     * componentRefMap.
     */
    private setIncomingModalLayoutStack(incomingModalLayoutStack: IFullModalLayout[] = []): void {
        this.modalLayoutStack = incomingModalLayoutStack;

        this.setBreadcrumbs(incomingModalLayoutStack);
        this.destroyComponentRefs(incomingModalLayoutStack);
        this.addToComponentRefMap(incomingModalLayoutStack);
    }

    /**
     * Set a stack of breadcrumb objects in-sync with the list of modals to render.
     */
    private setBreadcrumbs(incomingModalLayoutStack: IFullModalLayout[]): void {
        const breadcrumbStack: IBreadcrumb[] = [];
        const breadcrumbMap: Map<IFullModalLayout, IBreadcrumb> = new Map();

        incomingModalLayoutStack.forEach(modalLayout => {
            const { getName, getDescription } = modalLayout;

            let breadcrumb: IBreadcrumb = {
                breadcrumbName$: new BehaviorSubject(''),
                breadcrumbDescription$: new BehaviorSubject(''),
                getName,
                getDescription,
            };

            if (this.breadcrumbMap.has(modalLayout)) {
                breadcrumb = this.breadcrumbMap.get(modalLayout);
            }

            breadcrumbStack.push(breadcrumb);
            breadcrumbMap.set(modalLayout, breadcrumb);
        });

        this.breadcrumbStack = breadcrumbStack;
        this.breadcrumbMap = breadcrumbMap;
    }

    /**
     * Get the scrollable body element from the currently rendered modal config.
     */
    private getScrollableBody(): HTMLElement | null {
        return this.elementRef.nativeElement.querySelector('.full-modal-config__body');
    }

    /**
     * Save the current scroll position for the given modal layout.
     */
    private saveScrollPosition(modalLayout: IFullModalLayout): void {
        const scrollableBody = this.getScrollableBody();

        if (scrollableBody) {
            this.scrollPositionMap.set(modalLayout, scrollableBody.scrollTop);
        }
    }

    /**
     * Restore the scroll position for the given modal layout.
     */
    private restoreScrollPosition(modalLayout: IFullModalLayout): void {
        const scrollTop = this.scrollPositionMap.get(modalLayout);

        if (scrollTop !== undefined) {
            // Use setTimeout to ensure the DOM is fully rendered before restoring scroll position
            setTimeout(() => {
                const scrollableBody = this.getScrollableBody();

                if (scrollableBody) {
                    scrollableBody.scrollTop = scrollTop;
                }
            });
        }
    }

    /**
     * Renders a config componentRef (and preview componentRef if it exists) onto their respective
     * directives.
     */
    private renderComponents(): void {
        // Save scroll position of the previous modal before detaching
        if (this.previousModalLayout && this.previousModalLayout !== this.currentModalLayout) {
            this.saveScrollPosition(this.previousModalLayout);
        }

        this.fullModalConfigViewContainerRef.detach();
        this.fullModalPreviewContainerRef.detach();

        if (this.currentModalLayout) {
            const {
                configComponentRef,
                previewComponentRef,
            } = this.componentRefMap.get(this.currentModalLayout);

            FullModalComponent.renderComponentRef(
                configComponentRef,
                this.fullModalConfigViewContainerRef,
            );

            if (previewComponentRef) {
                FullModalComponent.renderComponentRef(
                    previewComponentRef,
                    this.fullModalPreviewContainerRef,
                );
            }

            // Restore scroll position if returning to a previously viewed modal
            if (this.scrollPositionMap.has(this.currentModalLayout)) {
                this.restoreScrollPosition(this.currentModalLayout);
            // If not returning to a previously viewed modal and scrollToSelector is set, scroll to and focus on the
            // element
            } else if (this.currentModalLayout.scrollToSelector) {
                this.scrollToAndFocusElement(this.currentModalLayout.scrollToSelector);
            }
        }

        // Update the previous modal layout reference
        this.previousModalLayout = this.currentModalLayout;
    }

    /**
     * Handles a new incoming stack of modalLayouts.
     */
    private updateModals = (incomingModalLayoutStack: IFullModalLayout[] = []): void => {
        this.setIncomingModalLayoutStack(incomingModalLayoutStack);
        this.renderComponents();
    };

    /**
     * Destroys componentRefs that no longer exist in the modalLayoutStack.
     */
    private destroyComponentRefs(incomingModalLayoutStack: IFullModalLayout[] = []): void {
        this.componentRefMap.forEach((componentRefs, modalLayout): void => {
            if (!incomingModalLayoutStack.includes(modalLayout)) {
                const {
                    configComponentRef,
                    previewComponentRef,
                } = componentRefs;

                configComponentRef.destroy();

                if (previewComponentRef) {
                    previewComponentRef.destroy();
                }

                this.componentRefMap.delete(modalLayout);
                this.scrollPositionMap.delete(modalLayout);
            }
        });
    }

    /**
     * Adds a modalLayout and its configComponentRef and previewComponentRef to the componentRefMap.
     */
    private addToComponentRefMap(incomingModalLayoutStack: IFullModalLayout[] = []): void {
        incomingModalLayoutStack.forEach((modalLayout: IFullModalLayout): void => {
            if (!this.componentRefMap.has(modalLayout)) {
                const componentRefs: IFullModalLayoutComponentRefs = {
                    configComponentRef: this.createModalComponentInstance(
                        modalLayout,
                        this.fullModalConfigViewContainerRef,
                    ),
                    previewComponentRef: null,
                };

                // Preview component's parent injector should be the modal component's injector,
                // so that we can list a service in the modal's provider
                // (mostly for inter-component communication),
                // and the preview component will be able to use the same instance.
                componentRefs.previewComponentRef = this.createPreviewComponentInstance(
                    modalLayout,
                    componentRefs.configComponentRef,
                );

                this.componentRefMap.set(modalLayout, componentRefs);
            }
        });
    }

    /**
     * Creates and returns an instance of the preview component if previewComponent props is
     * passed.
     * returns null otherwise.
     */
    private createPreviewComponentInstance(
        modalLayout: IFullModalLayout,
        modalComponentRef: ComponentRef<Component>,
    ): ComponentRef<Component> | null {
        const { previewComponent, previewComponentProps = {} } = modalLayout;

        if (!previewComponent) {
            return null;
        }

        const componentFactory =
            this.componentFactoryResolver.resolveComponentFactory(previewComponent);

        const componentRef = componentFactory.create(modalComponentRef.injector);

        attachComponentBindings(componentRef, previewComponentProps);

        return componentRef;
    }

    /**
     * Creates an instance of the config component. If editable.id exists as a
     * componentProp, set it as the itemId provider so that the modal component and its children
     * have access to it through injection.
     */
    private createModalComponentInstance(
        modalLayout: IFullModalLayout,
        viewContainerRef: ViewContainerRef,
    ): ComponentRef<Component> {
        const { component, componentProps = {} } = modalLayout;
        const breadcrumb = this.breadcrumbMap.get(modalLayout);
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
        const injector = Injector.create({
            parent: viewContainerRef.injector,
            providers: [
                {
                    provide: ITEM_ID_TOKEN,
                    useValue: componentProps.editable?.id,
                },
                {
                    provide: BREADCRUMB_NAME$,
                    useValue: breadcrumb.breadcrumbName$,
                },
                {
                    provide: BREADCRUMB_DESCRIPTION$,
                    useValue: breadcrumb.breadcrumbDescription$,
                },
            ],
        });

        const componentRef = componentFactory.create(injector);

        attachComponentBindings(componentRef, componentProps);

        return componentRef;
    }

    /**
     * Scroll to and focus on a specific element within the modal content.
     * Use setTimeout to wait for the DOM to be fully rendered before querying.
     */
    private scrollToAndFocusElement(selector: string): void {
        setTimeout(() => {
            const { configComponentRef } = this.componentRefMap.get(this.currentModalLayout) || {};
            const containerElement = configComponentRef?.location.nativeElement as HTMLElement;
            const element = containerElement?.querySelector(selector);

            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                if (element instanceof HTMLElement) {
                    element.focus();
                }
            }
        });
    }
}
